/**
 * The honest answer for every section with no real data source yet
 * (docs/architecture/web-not-live-sections.md).
 *
 * `HttpApiClient` delegates the methods on its not-live list here: every read
 * answers an empty dataset in the shape its screen expects, with
 * `meta.source: 'not_live'`, and every mutation is refused with an
 * `ApiError` whose code is `not_live`, so nothing can silently succeed.
 * Nothing here is generated: empty means empty, never zero-filled or demo.
 * When a section's read model lands, its methods leave the list and this
 * client, and the compiler points at each screen gate (`useNotLive`) still
 * naming them.
 */

import { ApiError } from '@/lib/api'

import type { MarketActivitySummary, MarketPricingSummary, MonitoringStatus } from './market'
import type { NotLiveApi } from './HttpApiClient'
import type { DataMeta, Envelope } from './types'

/** The `ApiError.code` a refused not-live mutation carries. */
export const NOT_LIVE_CODE = 'not_live'
export const NOT_LIVE_MESSAGE = 'This section is not live yet'

/** 409: the request is well-formed, but this section cannot accept it in its current state. */
export function notLiveError(): ApiError {
  return new ApiError(409, NOT_LIVE_CODE, NOT_LIVE_MESSAGE)
}

export function isNotLiveError(error: unknown): boolean {
  return error instanceof ApiError && error.code === NOT_LIVE_CODE
}

function notLiveMeta(): DataMeta {
  return { source: 'not_live', lastUpdatedAt: null }
}

function empty<T>(data: T): Promise<Envelope<T>> {
  return Promise.resolve({ meta: notLiveMeta(), data })
}

function refuse(): Promise<never> {
  return Promise.reject(notLiveError())
}

export class NotLiveApiClient implements NotLiveApi {
  // --- insights ------------------------------------------------------------------

  recommendations() {
    return empty([])
  }

  dismissRecommendation(): Promise<void> {
    return refuse()
  }

  automations() {
    return empty([])
  }

  // --- automation ------------------------------------------------------------------

  triggerDefinitions() {
    return Promise.resolve([])
  }

  actionDefinitions() {
    return Promise.resolve([])
  }

  workflows() {
    return empty([])
  }

  workflow() {
    return empty(null)
  }

  setWorkflowStatus() {
    return refuse()
  }

  scheduledActions() {
    return empty([])
  }

  cancelScheduledAction() {
    return refuse()
  }

  automationActivity() {
    return empty([])
  }

  // --- reports ---------------------------------------------------------------------

  savedReports() {
    return empty([])
  }

  savedReport() {
    return empty(null)
  }

  createSavedReport() {
    return refuse()
  }

  duplicateSavedReport() {
    return refuse()
  }

  scheduleSavedReport() {
    return refuse()
  }

  reportRows() {
    return empty([])
  }

  exports() {
    return empty([])
  }

  requestExport() {
    return refuse()
  }

  // --- market intelligence -----------------------------------------------------------

  marketCompetitors() {
    return empty([])
  }

  marketTrackedCompetitions() {
    return empty([])
  }

  marketObservations() {
    return empty([])
  }

  marketActivitySummary() {
    return empty<MarketActivitySummary>({ newLaunches: 0, closingSoon: 0, priceChanges: 0, categoryTrends: 0 })
  }

  marketPricingSummary() {
    return empty<MarketPricingSummary>({
      averageTicketPrice: 0,
      averageMaxEntries: 0,
      volume: 0,
      instantWinShare: 0,
      commonCategories: [],
      promotionalPatterns: [],
      byCategory: [],
    })
  }

  marketPrizeTrends() {
    return empty([])
  }

  marketMonitoringStatus() {
    return empty<MonitoringStatus>({ enabled: false, note: '' })
  }

  addMarketCompetitor() {
    return refuse()
  }

  importMarketCompetitorsCsv() {
    return refuse()
  }

  addTrackedCompetition() {
    return refuse()
  }

  importTrackedCompetitionsCsv() {
    return refuse()
  }

  // --- listings ------------------------------------------------------------------------

  listings() {
    return empty([])
  }

  listing() {
    return empty(null)
  }

  createListing() {
    return refuse()
  }

  updateListing() {
    return refuse()
  }

  generateListingContent() {
    return refuse()
  }

  setListingApproval() {
    return refuse()
  }

  publishListing() {
    return refuse()
  }

  listingTemplates() {
    return empty([])
  }

  saveListingTemplate() {
    return refuse()
  }
}
